/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import { css as cssUnbounded } from '@compiled/react';
import { useIntl } from 'react-intl';

import { css, jsx } from '@atlaskit/css';
import { akEditorFloatingPanelZIndex } from '@atlaskit/editor-shared-styles';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { token } from '@atlaskit/tokens';
import VisuallyHidden from '@atlaskit/visually-hidden/visually-hidden';

import { useElementBreakpoints } from '../../hooks/useElementBreakpoints';

import {
	ErrorButton,
	ErrorButtonContainer,
	ErrorFloatingContainer,
	ErrorMessage,
} from './error-screen-parts';
import messages from './messages';

/**
 * the z-index logic is duplicated from editor-plugin-ai/ModalRegion
 */

// Where do we get this magic number 500?
// - The lowest product z-index we need to be below is 500 from the Feedback Collector.
// As well as the Editor toolbar popup menu, which is also 500.
// This allows us to be above the floating toolbar, but below the feedback collector.
// +2 because tables floating toolbar is +1.
const MAX_Z_INDEX = 500;

// Where do we get this magic number 402?
// - The highest product z-index we need to be above is 401 from the floating toolbar*.
// *in Confluence and Bitbucket. It is 600px in Atlas & 511 in Jira, however that is a more niche case than the Feedback Collector,
// so this is a known trade-off until we have a better solution.
const MIN_Z_INDEX = 402;

// Sets the z-index so it is not greater than the maximum, and less than the minimum.
const akAiModalRegionZIndex = Math.min(
	MAX_Z_INDEX,
	Math.max(MIN_Z_INDEX, akEditorFloatingPanelZIndex + 2),
);

type Props = {
	inContentComponent?: boolean;
	isAIFC?: boolean;
	onCancel: () => void;
	text: string | React.ReactNode;
};

const errorScreenCSS = css({
	position: 'absolute',
});

const errorScreenFixedCSS = css({
	position: 'fixed',
	bottom: token('space.200'),
	left: 0,
	width: '100%',
});

const errorScreenCSSUnbounded = cssUnbounded({
	zIndex: akAiModalRegionZIndex,
});

export const UnhandledErrorScreen = ({
	onCancel,
	text,
	inContentComponent,
	isAIFC,
}: Props): JSX.Element => {
	const { formatMessage } = useIntl();
	const [setBreakpointsElement, { breakpoint }] = useElementBreakpoints();
	const [width, setWidth] = useState<number | undefined>(undefined);

	// when this is rendered in the content component, we have to calc it's width
	const onDivRef = useCallback(
		(node: HTMLDivElement) => {
			if (
				inContentComponent &&
				!width &&
				node &&
				node.parentNode instanceof HTMLElement &&
				isAIFC
			) {
				setWidth(node.parentNode.clientWidth);
			}
		},
		[inContentComponent, width, isAIFC],
	);

	const cnt = (
		<ErrorFloatingContainer ref={setBreakpointsElement} breakpoint={breakpoint} isAIFC={isAIFC}>
			<VisuallyHidden>{formatMessage(messages.ariaErrorTitle)}</VisuallyHidden>
			<ErrorMessage breakpoint={breakpoint} isAIFC={isAIFC}>
				{text}
			</ErrorMessage>
			<ErrorButtonContainer isAIFC={isAIFC}>
				<ErrorButton
					autoFocus
					testId={'cancel'}
					onClick={onCancel}
					appearance="primary"
					isAIFC={isAIFC}
				>
					{formatMessage(isAIFC ? messages.closeLabel : messages.cancelLabel)}
				</ErrorButton>
			</ErrorButtonContainer>
		</ErrorFloatingContainer>
	);

	// On the xstate-migration path, use position: fixed with display: grid so the error
	// screen pins to the viewport bottom and ErrorFloatingContainer's justifySelf/width
	// styles work correctly. This replaces the old position: absolute + JS width approach.
	const useFixedPositioning =
		inContentComponent &&
		isAIFC &&
		expValEquals('platform_editor_ai_xstate_migration', 'isEnabled', true);

	// when this is rendered in the content component, we have to add a wrapper div to apply some styles like position and zIndex
	// for z-index
	// - this will ensure it's applied properly because passing is via css to ErrorFloatingContainer won't work
	// - the z-index will be compiled to a css custom property, but the css custom property is undefined
	// - normally, the css custom property will be defined on the same dom element
	if (inContentComponent && isAIFC) {
		return useFixedPositioning ? (
			<div css={[errorScreenFixedCSS, errorScreenCSSUnbounded]}>{cnt}</div>
		) : (
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			<div ref={onDivRef} css={[errorScreenCSS, errorScreenCSSUnbounded]} style={{ width }}>
				{cnt}
			</div>
		);
	}

	return cnt;
};
